import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { panelSx, radius, type Tone, TONES, toneText } from '@/theme/tokens';

export function Dot({ tone, color, size = 8 }: { tone?: Tone; color?: string; size?: number }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flex: 'none',
        display: 'inline-block',
        bgcolor: color ?? TONES[tone ?? 'neutral'].main,
      }}
    />
  );
}

/**
 * Small rounded result tag, e.g. "exit 0" / "Timed out". `dot` gives it the
 * Bulk Shell badge look: a leading dot in its colour.
 */
export function TonePill({ tone, dot = false, children }: { tone: Tone; dot?: boolean; children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        fontSize: 12,
        borderRadius: '20px',
        color: toneText(tone),
        bgcolor: alpha(TONES[tone].main, 0.14),
        ...(dot
          ? {
              gap: '6px',
              fontWeight: 500,
              px: 1,
              py: '2px',
              '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' },
            }
          : { fontWeight: 600, px: 1.25, py: '3px' }),
      }}
    >
      {children}
    </Box>
  );
}

/**
 * One numbered step of the new-run form, open or summarised.
 *
 * Only one step is open at a time. Every control used to be on screen at once
 * — command, timeout, concurrency, target, filters, preview table, rollout,
 * waves, fail limit, offline handling — which made a page you had to scroll
 * past rather than read. A step that is settled collapses to the decision it
 * holds, so what is left open is the thing you are actually choosing.
 *
 * `summary` is what the collapsed step says. Omit `open` to keep the step
 * permanently expanded.
 */
export function StepCard({
  step,
  title,
  aside,
  summary,
  open = true,
  onOpen,
  children,
}: {
  step: number;
  title: string;
  aside?: ReactNode;
  summary?: ReactNode;
  open?: boolean;
  onOpen?: () => void;
  children: ReactNode;
}) {
  const collapsible = !!onOpen;

  const marker = (
    <Box
      component="span"
      sx={{
        width: 22,
        height: 22,
        flex: 'none',
        borderRadius: '50%',
        bgcolor: open ? 'text.primary' : 'action.selected',
        color: open ? 'background.paper' : 'text.secondary',
        fontSize: 12,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {step}
    </Box>
  );

  const heading = (
    <>
      {marker}
      <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
    </>
  );

  return (
    <Box
      component="section"
      sx={{ ...panelSx, boxShadow: 0, p: open ? 2.5 : 0, display: 'flex', flexDirection: 'column', gap: open ? 1.75 : 0 }}
    >
      {collapsible && !open ? (
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          aria-expanded={false}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            width: '100%',
            minHeight: 44,
            px: 2.5,
            py: 1.5,
            border: 0,
            borderRadius: `${radius.lg}px`,
            bgcolor: 'transparent',
            font: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {heading}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, fontSize: 12, color: 'text.secondary' }}>
            {summary}
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', whiteSpace: 'nowrap' }}>Edit</Box>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            {heading}
            {aside && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>{aside}</Box>}
          </Box>
          {children}
        </>
      )}
    </Box>
  );
}
